import { useCallback, useMemo } from 'react';
import { currentDomainConfig } from '../domain-config';
import { CountrySlug, LocaleCode } from '../utils/locale';

export interface CountryInfo {
    code: string;
    slug: CountrySlug;
    language: LocaleCode;
    phoneCode: number;
    name: string;
    nativeName: string;
    urlParty: string;
    urlPartyFaq: string;
    urlSportKidz: string;
    urlSummerCamp: string;
    urlProfile: string;
    urlOnboarding: string;
    urlNext: string;
    urlShop: string;
    urlNewPartyBooking: string;
}

/**
 * Hook to return a translated list of all supported countries with code, locale and phone codes.
 */
export function useCountries() {
    const countries: CountryInfo[] = useMemo(() => {
        return [
            {
                code: 'SE',
                language: 'sv',
                slug: 'sweden',
                phoneCode: 46,
                name: 'Sweden',
                nativeName: 'Sverige',
                urlParty: `${process.env.REACT_APP_BASE_URL_PARTY_SE ?? ''}/sv`,
                urlPartyFaq: process.env.REACT_APP_URL_PARTY_FAQ_SE ?? '',
                urlSportKidz: process.env.REACT_APP_URL_SPORT_KIDZ_SE ?? '',
                urlSummerCamp: process.env.REACT_APP_URL_SUMMER_CAMP_SE ?? '',
                urlProfile: `${process.env.REACT_APP_BASE_URL_NEXT_SE ?? ''}/account`,
                urlOnboarding: `${process.env.REACT_APP_BASE_URL_NEXT_SE ?? ''}/onboarding`,
                urlNext: process.env.REACT_APP_BASE_URL_NEXT_SE ?? '',
                urlShop: `${process.env.REACT_APP_BASE_URL_ORDER_SE ?? ''}/sv/shop`,
                urlNewPartyBooking: `${process.env.REACT_APP_URL_NEW_PARTY_BOOKING_SE ?? ''}`,
            },
            {
                code: 'NO',
                language: 'nb',
                slug: 'norway',
                phoneCode: 47,
                name: 'Norway',
                nativeName: 'Norge',
                urlParty: `${process.env.REACT_APP_BASE_URL_PARTY_NO ?? ''}/nb`,
                urlPartyFaq: process.env.REACT_APP_URL_PARTY_FAQ_NO ?? '',
                urlSportKidz: process.env.REACT_APP_URL_SPORT_KIDZ_NO ?? '',
                urlSummerCamp: process.env.REACT_APP_URL_SUMMER_CAMP_NO ?? '',
                urlProfile: `${process.env.REACT_APP_BASE_URL_NEXT_NO ?? ''}/account`,
                urlOnboarding: `${process.env.REACT_APP_BASE_URL_NEXT_NO ?? ''}/onboarding`,
                urlNext: process.env.REACT_APP_BASE_URL_NEXT_NO ?? '',
                urlShop: `${process.env.REACT_APP_BASE_URL_ORDER_NO ?? ''}/nb/shop`,
                urlNewPartyBooking: `${process.env.REACT_APP_URL_NEW_PARTY_BOOKING_NO ?? ''}`,
            },
            {
                code: 'DK',
                language: 'da',
                slug: 'denmark',
                phoneCode: 45,
                name: 'Denmark',
                nativeName: 'Danmark',
                urlParty: `${process.env.REACT_APP_BASE_URL_PARTY_DK ?? ''}/da`,
                urlPartyFaq: process.env.REACT_APP_URL_PARTY_FAQ_DK ?? '',
                urlSportKidz: process.env.REACT_APP_URL_SPORT_KIDZ_DK ?? '',
                urlSummerCamp: process.env.REACT_APP_URL_SUMMER_CAMP_DK ?? '',
                urlProfile: `${process.env.REACT_APP_BASE_URL_NEXT_DK ?? ''}/account`,
                urlOnboarding: `${process.env.REACT_APP_BASE_URL_NEXT_DK ?? ''}/onboarding`,
                urlNext: process.env.REACT_APP_BASE_URL_NEXT_DK ?? '',
                urlShop: `${process.env.REACT_APP_BASE_URL_ORDER_DK ?? ''}/da/shop`,
                urlNewPartyBooking: `${process.env.REACT_APP_URL_NEW_PARTY_BOOKING_DK ?? ''}`,
            },
            {
                code: 'FI',
                language: 'fi',
                slug: 'finland',
                phoneCode: 358,
                name: 'Finland',
                nativeName: 'Suomi',
                urlParty: `${process.env.REACT_APP_BASE_URL_PARTY_FI ?? ''}/fi`,
                urlPartyFaq: process.env.REACT_APP_URL_PARTY_FAQ_FI ?? '',
                urlSportKidz: process.env.REACT_APP_URL_SPORT_KIDZ_FI ?? '',
                urlSummerCamp: process.env.REACT_APP_URL_SUMMER_CAMP_FI ?? '',
                urlProfile: `${process.env.REACT_APP_BASE_URL_NEXT_FI ?? ''}/account`,
                urlOnboarding: `${process.env.REACT_APP_BASE_URL_NEXT_FI ?? ''}/onboarding`,
                urlNext: process.env.REACT_APP_BASE_URL_NEXT_FI ?? '',
                urlShop: `${process.env.REACT_APP_BASE_URL_ORDER_FI ?? ''}/fi/shop`,
                urlNewPartyBooking: `${process.env.REACT_APP_URL_NEW_PARTY_BOOKING_FI ?? ''}`,
            },
            {
                code: 'DE',
                language: 'de',
                slug: 'germany',
                phoneCode: 49,
                name: 'Germany',
                nativeName: 'Deutschland',
                urlParty: `${process.env.REACT_APP_BASE_URL_PARTY_DE ?? ''}/de`,
                urlPartyFaq: process.env.REACT_APP_URL_PARTY_FAQ_DE ?? '',
                urlSportKidz: process.env.REACT_APP_URL_SPORT_KIDZ_DE ?? '',
                urlSummerCamp: process.env.REACT_APP_URL_SUMMER_CAMP_DE ?? '',
                urlProfile: `${process.env.REACT_APP_BASE_URL_NEXT_DE ?? ''}/account`,
                urlOnboarding: `${process.env.REACT_APP_BASE_URL_NEXT_DE ?? ''}/onboarding`,
                urlNext: process.env.REACT_APP_BASE_URL_NEXT_DE ?? '',
                urlBookEntrance: `${process.env.REACT_APP_BASE_URL_NEXT_DE ?? ''}/book-entrance`,
                urlShop: `${process.env.REACT_APP_BASE_URL_ORDER_DE ?? ''}/de/shop`,
                urlNewPartyBooking: `${process.env.REACT_APP_URL_NEW_PARTY_BOOKING_DE ?? ''}`,
            },
        ];
    }, []);

    const currentCountry: CountryInfo = useMemo(() => {
        return countries.find((country) => country.slug === currentDomainConfig.countrySlug) as CountryInfo;
    }, [countries]);

    const getCountryByCode = useCallback(
        (code: string) => {
            return countries.find((country) => country.code === code) ?? null;
        },
        [countries],
    );

    const getCountryByCulture = useCallback(
        (culture: string) => {
            const [, code] = culture.split('-');
            return countries.find((country) => country.code === code) ?? null;
        },
        [countries],
    );

    return { currentCountry, countries, getCountryByCode, getCountryByCulture };
}
